/**
 * Brand form validation utilities
 */

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validate brand name (min 2 characters)
 */
export const validateBrandName = (name: string): ValidationResult => {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: "Brand name is required" };
  }
  if (name.trim().length < 2) {
    return { isValid: false, error: "Brand name must be at least 2 characters" };
  }
  return { isValid: true };
};

/**
 * The address a business is known by: protocol and host and path, without the
 * query string or fragment. Owners paste what their browser shows, which for a
 * franchise page copied from Google Business Profile is the canonical URL plus
 * a tail of tracking parameters, and that tail is neither part of the identity
 * nor something the crawler should carry.
 */
export const canonicalWebsiteUrl = (url: string): string => {
  const trimmed = (url || "").trim();
  if (!trimmed) return "";
  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withProtocol);
    parsed.search = "";
    parsed.hash = "";
    return parsed.toString();
  } catch {
    return trimmed.split(/[?#]/)[0];
  }
};

/**
 * The message a customer sees when what they typed is not a colour we can use.
 * Names the shape and shows one, because "invalid" on its own is what left the
 * original reporter guessing for a couple of tries (SGL-342).
 */
export const HEX_COLOR_ERROR = "Use a 6 digit hex colour, for example #648B7F";

/**
 * The colour a customer means, written the one way CSS understands it.
 *
 * People type a hex the way they copied it — out of a brand guide, a design
 * tool, a chat message — so the hash is optional, the case is whatever it was,
 * and there is often a space clinging to either end. All of those are the same
 * colour, and the only reason `648B7F` ever failed was that we stored the
 * keystrokes instead of the colour (SGL-342).
 *
 * Returns the canonical `#RRGGBB` form, or null when the input is not a colour
 * at all — the caller is expected to say so rather than save it quietly.
 */
export const canonicalHexColor = (value: string): string | null => {
  const digits = (value || "").trim().replace(/^#/, "");
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(digits)) return null;
  // `#fff` is shorthand for `#ffffff`; expand it so everything downstream — the
  // native swatch, the stored record, the compositor — handles one length only.
  const full = digits.length === 3 ? digits.replace(/./g, (c) => c + c) : digits;
  return `#${full.toUpperCase()}`;
};

/**
 * Validate a brand colour
 */
export const validateHexColor = (value: string): ValidationResult => {
  if (canonicalHexColor(value) === null) {
    return { isValid: false, error: HEX_COLOR_ERROR };
  }
  return { isValid: true };
};

/**
 * Validate website URL format
 */
export const validateWebsiteUrl = (url: string): ValidationResult => {
  // Optional (SGL-448): many owner-led businesses have no website, or one we
  // can't read. A website improves what the agents know; it never blocks setup.
  if (!url || url.trim().length === 0) {
    return { isValid: true };
  }

  // Validate the canonical form: protocol stripped, query string and fragment
  // dropped, so a pasted tracking URL is judged on its host and path alone.
  const urlWithoutProtocol = canonicalWebsiteUrl(url).replace(/^https?:\/\//, "");

  // Basic URL validation
  const urlPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.([a-zA-Z]{2,}\.?)+([\/\w \.-]*)*\/?$/;

  if (!urlPattern.test(urlWithoutProtocol)) {
    return { isValid: false, error: "Please enter a valid website URL" };
  }

  return { isValid: true };
};

/**
 * Validate phone number (basic format check)
 */
export const validatePhoneNumber = (phone: string): ValidationResult => {
  if (!phone || phone.trim().length === 0) {
    return { isValid: true }; // Optional field
  }

  // Remove all non-digit characters for validation
  const digitsOnly = phone.replace(/\D/g, "");
  
  if (digitsOnly.length < 10) {
    return { isValid: false, error: "Phone number must be at least 10 digits" };
  }
  
  if (digitsOnly.length > 15) {
    return { isValid: false, error: "Phone number is too long" };
  }
  
  return { isValid: true };
};

/**
 * Validate service area (now required)
 */
export const validateServiceArea = (areas: string[]): ValidationResult => {
  if (!areas || areas.length === 0) {
    return { isValid: false, error: "Service area is required. Please add at least one location." };
  }
  
  // Check if all areas are valid (at least 3 characters)
  const invalidAreas = areas.filter(area => !area || area.trim().length < 3);
  if (invalidAreas.length > 0) {
    return { isValid: false, error: "Each service area must be at least 3 characters" };
  }
  
  return { isValid: true };
};

/**
 * Validate services offered
 */
export const validateServicesOffered = (services: string): ValidationResult => {
  if (!services || services.trim().length === 0) {
    return { isValid: true }; // Optional field
  }
  
  if (services.trim().length < 5) {
    return { isValid: false, error: "Please provide more detail about services" };
  }
  
  return { isValid: true };
};

/**
 * Validate tagline (max 120 characters)
 */
export const validateTagline = (tagline: string): ValidationResult => {
  if (!tagline || tagline.trim().length === 0) {
    return { isValid: true }; // Optional field
  }
  
  if (tagline.length > 120) {
    return { isValid: false, error: "Tagline must be 120 characters or less" };
  }
  
  return { isValid: true };
};

/**
 * Validate all form fields
 */
export const validateAllFields = (formData: {
  brandName: string;
  businessType: "SERVICE" | "PRODUCT" | "BOTH" | "";
  industry: string;
  website: string;
  phoneNumber: string;
  serviceArea: string[];
  servicesOffered: string[];
  productsOffered: string[];
  tagline: string;
  logoUrl: string;
  colors: Array<{ value: string; type: string }>;
  brandVoice: string;
  targetAudience: string;
  primaryCTA: string[];
  avoidWords: string[];
  mustIncludeWords: string[];
}): Record<string, string> => {
  const errors: Record<string, string> = {};

  const brandNameResult = validateBrandName(formData.brandName);
  if (!brandNameResult.isValid) {
    errors.brandName = brandNameResult.error!;
  }

  // Business Type is required
  if (!formData.businessType) {
    errors.businessType = "Business type is required";
  }

  // Conditionally require services or products based on business type
  if (formData.businessType === "SERVICE" && formData.servicesOffered.length === 0) {
    errors.servicesOffered = "At least one service is required for service businesses";
  }
  if (formData.businessType === "PRODUCT" && formData.productsOffered.length === 0) {
    errors.productsOffered = "At least one product is required for product businesses";
  }
  if (formData.businessType === "BOTH" && formData.servicesOffered.length === 0 && formData.productsOffered.length === 0) {
    errors.servicesOffered = "At least one service or product is required";
  }

  const websiteResult = validateWebsiteUrl(formData.website);
  if (!websiteResult.isValid) {
    errors.website = websiteResult.error!;
  }

  const phoneResult = validatePhoneNumber(formData.phoneNumber);
  if (!phoneResult.isValid) {
    errors.phoneNumber = phoneResult.error!;
  }

  const taglineResult = validateTagline(formData.tagline);
  if (!taglineResult.isValid) {
    errors.tagline = taglineResult.error!;
  }

  // Service Area is now required
  const serviceAreaResult = validateServiceArea(formData.serviceArea);
  if (!serviceAreaResult.isValid) {
    errors.serviceArea = serviceAreaResult.error!;
  }

  // Brand voice is optional (SGL-448): an empty voice blocked a new customer
  // from chatting at all. If one is written, it must say something.
  if (formData.brandVoice && formData.brandVoice.trim().length > 0 && formData.brandVoice.trim().length < 10) {
    errors.brandVoice = "Brand voice must be at least 10 characters";
  }

  return errors;
};
